/**
 * Dot — a single entity dot inside a Well.
 *
 * Visual spec (BRD §7.4):
 *   Expert:          green, r=3.2px
 *   Seeker:          slate-500, r=2.6px
 *   Stuck:           critical-red, pulsing
 *   Agent acting:    white border + green orbit ring
 *
 * Hover (BRD §7.5):
 *   - 8–10px transparent hit area
 *   - Pauses orbit animation on this dot only
 *   - Shows tooltip anchored to dot (not cursor)
 *   - Click → fires onSelect(dot)
 */
import { useState, useRef } from "react";

const RADIUS = {
  expert: 3.2,
  expert_candidate: 3.2,
  seeker: 2.6,
  session: 2.6,
  default: 2.8,
};

const HIT_AREA = 9; // transparent radius around the dot for easy hover

function DotTooltip({ dot, style }) {
  const timeLabel = dot.time_in_state_minutes != null
    ? dot.time_in_state_minutes < 60
      ? `${dot.time_in_state_minutes}m`
      : `${Math.round(dot.time_in_state_minutes / 60)}h`
    : "—";

  return (
    <div className="mc-dot-tooltip" style={style}>
      <div className="mc-dot-tooltip-name">{dot.name}</div>
      <div className="mc-dot-tooltip-meta">
        <span className="mc-dot-tooltip-kind">{dot.kind?.toUpperCase()}</span>
        <span className="mc-dot-tooltip-sep">·</span>
        <span className="mc-dot-tooltip-state">{dot.status}</span>
        <span className="mc-dot-tooltip-sep">·</span>
        <span className="mc-dot-tooltip-time mc-mono">{timeLabel}</span>
      </div>
      {dot.is_stuck && (
        <div className="mc-dot-tooltip-stuck">⚠ Over SLA</div>
      )}
      {dot.agent_acting && (
        <div className="mc-dot-tooltip-agent">
          Agent working: <strong>{dot.agent_acting}</strong>
        </div>
      )}
    </div>
  );
}

export default function Dot({ dot, x, y, onSelect }) {
  const [hovered, setHovered] = useState(false);
  const dotRef = useRef(null);

  const r = RADIUS[dot.kind] || RADIUS.default;
  const isExpert = dot.kind === "expert" || dot.kind === "expert_candidate";
  const isStuck = dot.is_stuck;
  const isActing = Boolean(dot.agent_acting);

  // Colors
  let fill = isExpert ? "var(--color-green)" : "var(--color-slate-500)";
  if (isStuck) fill = "var(--color-critical)";

  const strokeColor = isActing ? "#fff" : "transparent";
  const strokeWidth = isActing ? 0.5 : 0;

  // Tooltip positioning — stays inside canvas
  const tooltipStyle = {
    top: y - 50,
    left: x + 12,
  };

  return (
    <g
      ref={dotRef}
      className={`mc-dot${isStuck ? " mc-dot--stuck" : ""}${isActing ? " mc-dot--acting" : ""}${hovered ? " mc-dot--hovered" : ""}`}
      style={{ cursor: "pointer" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect?.(dot)}
      aria-label={dot.name}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onSelect?.(dot)}
    >
      {/* Hit area — transparent circle for easy hover */}
      <circle cx={x} cy={y} r={HIT_AREA} fill="transparent" />

      {/* Agent acting orbit ring */}
      {isActing && !hovered && (
        <circle
          cx={x}
          cy={y}
          r={r + 4}
          fill="none"
          stroke="var(--color-green)"
          strokeWidth={1}
          strokeDasharray="3 2"
          className="mc-dot-orbit"
          opacity={0.7}
        />
      )}

      {/* Main dot */}
      <circle
        cx={x}
        cy={y}
        r={r}
        fill={fill}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        className={isStuck ? "mc-dot-pulse" : undefined}
      />

      {/* Tooltip (portal-style, rendered at canvas level via foreignObject for simplicity) */}
      {hovered && (
        <foreignObject
          x={x + 12}
          y={y - 50}
          width={180}
          height={90}
          style={{ overflow: "visible" }}
        >
          <DotTooltip dot={dot} style={{}} />
        </foreignObject>
      )}
    </g>
  );
}
